import type { ElasoRoleRef } from "@elaso/shared/types/authorization";
import type { UserProfile } from "@elaso/shared/schemas/profile";
import { captureException } from "@sentry/react";
import { Circle, Mic, Search, UserPlus, Users, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";

const getDisplayName = (user: UserProfile) => {
	return user.displayName || "User";
};

const getInitials = (user: UserProfile) => {
	const first = user.givenName?.[0] || "";
	const last = user.familyName?.[0] || "";
	return (first + last).toUpperCase() || "U";
};

const getProgramRoles = (
	roles: ElasoRoleRef[],
	roster: Map<string, { id: string; name: string }>
): string[] => {
	const programRoles: string[] = [];
	for (const ref of roles) {
		if (ref.kind !== "named") continue;
		const name = roster.get(ref.id)?.name;
		if (!name) continue;
		const parts = name.split("--");
		const lastPart = parts.at(-1) ?? "";

		if (
			!(
				name.includes("cohort--") ||
				name.includes("team--") ||
				name.includes("oi-table--")
			) &&
			lastPart &&
			!programRoles.includes(lastPart)
		) {
			programRoles.push(lastPart);
		}
	}
	return programRoles;
};

export function BroadcastSpeakerManager() {
	const [isOpen, setIsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState("");

	const users = useStore(coreStore, (state) => state.users);
	const rolesRoster = useStore(coreStore, (state) => state.roles);
	const broadcast = useStore(coreStore, (state) => state.broadcast);
	const inviteSpeaker = useStore(coreStore, (state) => state.inviteSpeaker);
	const removeSpeaker = useStore(coreStore, (state) => state.removeSpeaker);
	const broadcastRoom = useStore(uiStore, (state) => state.broadcastRoom);
	// Role badges and role-name search read other users' roles, which the connect
	// chain no longer loads, only the caller's. The full matrix is lazy-loaded on
	// open, fetch-once guarded, and self-heals if a reconnect clears it while the
	// panel is open.
	const allRolesLoaded = useStore(coreStore, (state) => state.allRolesLoaded);
	const allRolesLoading = useStore(coreStore, (state) => state.allRolesLoading);
	const fetchAllUserRoles = useStore(
		coreStore,
		(state) => state.fetchAllUserRoles
	);

	useEffect(() => {
		if (isOpen && !(allRolesLoaded || allRolesLoading)) {
			fetchAllUserRoles();
		}
	}, [isOpen, allRolesLoaded, allRolesLoading, fetchAllUserRoles]);

	const invitedSpeakers = broadcast?.invitedSpeakers ?? [];

	const [liveParticipantIds, setLiveParticipantIds] = useState<Set<string>>(
		new Set()
	);

	useEffect(() => {
		if (!broadcastRoom) {
			setLiveParticipantIds(new Set());
			return;
		}

		const updateLiveParticipants = () => {
			const ids = new Set<string>();
			for (const participant of broadcastRoom.remoteParticipants.values()) {
				if (participant.permissions?.canPublish) {
					ids.add(participant.identity);
				}
			}
			setLiveParticipantIds(ids);
		};

		updateLiveParticipants();

		broadcastRoom.on("participantConnected", updateLiveParticipants);
		broadcastRoom.on("participantDisconnected", updateLiveParticipants);
		broadcastRoom.on("participantPermissionsChanged", updateLiveParticipants);

		return () => {
			broadcastRoom.off("participantConnected", updateLiveParticipants);
			broadcastRoom.off("participantDisconnected", updateLiveParticipants);
			broadcastRoom.off(
				"participantPermissionsChanged",
				updateLiveParticipants
			);
		};
	}, [broadcastRoom]);

	const usersList = useMemo(
		() => Array.from(users.values()).map((e) => e.profile),
		[users]
	);

	const invitedUsers = useMemo(
		() =>
			usersList
				.filter((user) => invitedSpeakers.includes(user.firebaseUid))
				.sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b))),
		[usersList, invitedSpeakers]
	);

	const availableUsers = useMemo(
		() =>
			usersList
				.filter(
					(user) =>
						!invitedSpeakers.includes(user.firebaseUid) &&
						user.firebaseUid !== broadcast?.startedBy
				)
				.sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b))),
		[usersList, invitedSpeakers, broadcast?.startedBy]
	);

	const filteredAvailableUsers = useMemo(() => {
		if (!searchQuery.trim()) return availableUsers;
		const query = searchQuery.toLowerCase();
		return availableUsers.filter((user) => {
			const displayName = getDisplayName(user).toLowerCase();
			const userEntry = users.get(user.firebaseUid);
			const roles = userEntry
				? getProgramRoles(userEntry.roles, rolesRoster)
				: [];
			const rolesMatch = roles.some((role) =>
				role.toLowerCase().includes(query)
			);
			return displayName.includes(query) || rolesMatch;
		});
	}, [availableUsers, searchQuery, users, rolesRoster]);

	const handleInvite = async (userFirebaseUid: string) => {
		setIsLoading(userFirebaseUid);
		try {
			await inviteSpeaker(userFirebaseUid);
			const entry = users.get(userFirebaseUid);
			toast.success(
				`Invited ${entry ? getDisplayName(entry.profile) : "user"} to speak`
			);
		} catch (error) {
			console.error("Failed to invite speaker:", error);
			if (SENTRY_ENABLED)
				captureException(error, {
					tags: { context: "broadcast_invite_speaker" },
				});
			toast.error("Failed to invite speaker");
		} finally {
			setIsLoading(null);
		}
	};

	const handleRemove = async (userFirebaseUid: string) => {
		setIsLoading(userFirebaseUid);
		try {
			await removeSpeaker(userFirebaseUid);
			const entry = users.get(userFirebaseUid);
			toast.success(
				`Removed ${entry ? getDisplayName(entry.profile) : "user"} from speakers`
			);
		} catch (error) {
			console.error("Failed to remove speaker:", error);
			if (SENTRY_ENABLED)
				captureException(error, {
					tags: { context: "broadcast_remove_speaker" },
				});
			toast.error("Failed to remove speaker");
		} finally {
			setIsLoading(null);
		}
	};

	return (
		<Popover onOpenChange={setIsOpen} open={isOpen}>
			<PopoverTrigger asChild>
				<Button className="text-foreground" size="sm" variant="outline">
					<Users className="mr-1 size-4" />
					Manage Speakers
					{invitedSpeakers.length > 0 && (
						<span className="ml-1 rounded-full bg-primary px-1.5 text-primary-foreground text-xs">
							{invitedSpeakers.length}
						</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				className="w-80 p-0"
				style={{ maxHeight: "min(500px, 80vh)" }}
			>
				<div className="flex h-full max-h-[inherit] flex-col">
					<div className="shrink-0 border-b p-3">
						<h4 className="font-medium text-sm">Manage Speakers</h4>
						<p className="text-muted-foreground text-xs">
							Invite members to speak in the broadcast
						</p>
					</div>

					<div className="min-h-0 flex-1 overflow-y-auto">
						{invitedUsers.length > 0 && (
							<div className="border-b p-2">
								<div className="mb-2 flex items-center gap-1 px-1">
									<Mic className="size-3 text-green-500" />
									<span className="font-medium text-muted-foreground text-xs">
										Invited Speakers ({invitedUsers.length})
									</span>
								</div>
								<div className="space-y-1">
									{invitedUsers.map((user) => {
										const isLive = liveParticipantIds.has(user.firebaseUid);
										const userEntry = users.get(user.firebaseUid);
										const programRoles = userEntry
											? getProgramRoles(userEntry.roles, rolesRoster)
											: [];
										return (
											<div
												className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-muted"
												key={user.firebaseUid}
											>
												<div className="flex min-w-0 flex-1 items-center gap-2">
													<Avatar className="size-6 shrink-0">
														{user.profilePhoto ? (
															<AvatarImage
																alt={getDisplayName(user)}
																src={user.profilePhoto}
															/>
														) : null}
														<AvatarFallback className="text-xs">
															{getInitials(user)}
														</AvatarFallback>
													</Avatar>
													<div className="flex min-w-0 flex-1 flex-col">
														<span className="truncate text-sm">
															{getDisplayName(user)}
														</span>
														{programRoles.length > 0 && (
															<span className="truncate text-muted-foreground text-xs">
																{programRoles.join(", ")}
															</span>
														)}
													</div>
												</div>
												<div className="flex shrink-0 items-center gap-1">
													<Badge
														className={`gap-1 text-[10px] ${
															isLive
																? "border-green-500/30 bg-green-500/10 text-green-500"
																: "border-yellow-500/30 bg-yellow-500/10 text-yellow-500"
														}`}
														variant="outline"
													>
														<Circle
															className={`size-1.5 fill-current ${
																isLive ? "animate-pulse" : ""
															}`}
														/>
														{isLive ? "Live" : "Pending"}
													</Badge>
													<Button
														className="size-6"
														disabled={isLoading === user.firebaseUid}
														onClick={() => handleRemove(user.firebaseUid)}
														size="icon"
														variant="ghost"
													>
														<X className="size-3" />
													</Button>
												</div>
											</div>
										);
									})}
								</div>
							</div>
						)}

						<div className="p-2">
							<div className="mb-2 flex items-center gap-1 px-1">
								<UserPlus className="size-3 text-muted-foreground" />
								<span className="font-medium text-muted-foreground text-xs">
									Invite to Speak
								</span>
							</div>
							<div className="relative mb-2">
								<Search className="-translate-y-1/2 absolute top-1/2 left-2 size-3.5 text-muted-foreground" />
								<Input
									className="h-8 pl-7 text-sm"
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder="Search by name or role..."
									value={searchQuery}
								/>
							</div>
							<div className="space-y-1">
								{filteredAvailableUsers.length === 0 ? (
									<p className="px-2 py-4 text-center text-muted-foreground text-sm">
										{searchQuery.trim()
											? "No users match your search"
											: "No users available to invite"}
									</p>
								) : (
									filteredAvailableUsers.map((user) => {
										const userEntry = users.get(user.firebaseUid);
										const programRoles = userEntry
											? getProgramRoles(userEntry.roles, rolesRoster)
											: [];
										return (
											<div
												className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-muted"
												key={user.firebaseUid}
											>
												<div className="flex min-w-0 flex-1 items-center gap-2">
													<Avatar className="size-6 shrink-0">
														{user.profilePhoto ? (
															<AvatarImage
																alt={getDisplayName(user)}
																src={user.profilePhoto}
															/>
														) : null}
														<AvatarFallback className="text-xs">
															{getInitials(user)}
														</AvatarFallback>
													</Avatar>
													<div className="flex min-w-0 flex-1 flex-col">
														<span className="truncate text-sm">
															{getDisplayName(user)}
														</span>
														{programRoles.length > 0 && (
															<span className="truncate text-muted-foreground text-xs">
																{programRoles.join(", ")}
															</span>
														)}
													</div>
												</div>
												<Button
													className="shrink-0"
													disabled={isLoading === user.firebaseUid}
													onClick={() => handleInvite(user.firebaseUid)}
													size="icon"
													variant="ghost"
												>
													<Mic className="size-4" />
												</Button>
											</div>
										);
									})
								)}
							</div>
						</div>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}
